// PresentationStandings — ALL pools on ONE screen (no rotation, no scroll).
// Rows are content-driven and compact; the font size is derived from the
// space available to the tallest pool so everything always fits. Uses the
// pre-computed rr.standings — no math here.
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Trophy } from '@phosphor-icons/react';
import FitText from './FitText';

const GREEN = '#0c4f38';
const GOLD = '#f2c94c';
const POOL_HEADER_H = 1.55; // × fs
const TABLE_HEADER_H = 1.35; // × fs
const ROW_H = 2.35; // × fs (two player lines)

function entryLines(entry, fallbackName) {
  if (!entry) return [fallbackName || '—'];
  if (entry.team_name) return [entry.team_name];
  const ps = [entry.player1_name, entry.player2_name].filter(Boolean);
  return ps.length ? ps : [entry.display_name || fallbackName || '—'];
}

export function fmtPct(v) {
  return v == null ? '—' : `${Number(v).toFixed(1)}%`;
}

function gridShape(n) {
  if (n <= 3) return { cols: n, rows: 1 };
  if (n <= 4) return { cols: 2, rows: 2 };
  if (n <= 6) return { cols: 3, rows: 2 };
  return { cols: 4, rows: Math.ceil(n / 4) };
}

function useSize(ref) {
  const [s, setS] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setS({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return s;
}

function PoolCard({ label, rows, entriesById, fs }) {
  const cols = `${fs * 1.9}px 1fr ${fs * 1.6}px ${fs * 1.6}px ${fs * 1.7}px ${fs * 1.7}px ${fs * 2.7}px`;
  return (
    <div className="border-2 rounded-2xl bg-white flex flex-col overflow-hidden self-start" style={{ borderColor: `${GOLD}` , boxShadow: '0 6px 18px rgba(1,53,35,0.12)' }} data-testid={`pv-standings-pool-${label}`}>
      <div className="shrink-0 flex items-center justify-between text-white" style={{ height: fs * POOL_HEADER_H, padding: `0 ${fs * 0.7}px`, background: GREEN }}>
        <div className="font-serif truncate" style={{ fontSize: fs * 0.95 }}>{label}</div>
        <div className="uppercase tracking-[0.24em] font-bold" style={{ fontSize: fs * 0.42, color: GOLD }}>Standings</div>
      </div>
      <div
        className="grid items-center uppercase tracking-[0.14em] font-bold text-[#6a766e] border-b border-[#e3dcd1] bg-[#fbfaf8]"
        style={{ gridTemplateColumns: cols, height: fs * TABLE_HEADER_H, padding: `0 ${fs * 0.7}px`, fontSize: fs * 0.44 }}
      >
        <div className="text-center">Rank</div><div>Team</div><div className="text-center">W</div><div className="text-center">L</div><div className="text-center">GW</div><div className="text-center">GL</div><div className="text-center">Game %</div>
      </div>
      {rows.map((row, i) => {
        const entry = entriesById[row.entry_id];
        const leader = row.rank === 1 && !row.tie_unresolved;
        const runnerUp = row.rank === 2 && !row.tie_unresolved;
        const trophyColor = leader ? '#d4a72c' : (runnerUp ? '#aab2b8' : null);
        const lines = entryLines(entry, row.display_name);
        return (
          <div
            key={row.entry_id}
            className={`grid items-center ${i < rows.length - 1 ? 'border-b border-[#efe9dd]' : ''} ${leader ? 'bg-[#fdf6e3]' : (runnerUp ? 'bg-[#f4f5f6]' : '')}`}
            style={{ gridTemplateColumns: cols, height: fs * ROW_H, padding: `0 ${fs * 0.7}px` }}
            data-testid={`pv-standings-row-${row.entry_id}`}
          >
            <div className="relative flex items-center justify-center tabular-nums font-bold" style={{ fontSize: fs * 0.9, color: leader ? '#b8860b' : (runnerUp ? '#5b6a72' : GREEN) }}>
              {trophyColor && (
                <Trophy
                  weight="fill"
                  className="absolute left-0"
                  style={{ color: trophyColor, fontSize: fs * 0.64 }}
                  data-testid={`pv-standings-row-${row.entry_id}-trophy`}
                />
              )}
              <span>{row.rank}</span>
            </div>
            <div className="h-full min-h-0 min-w-0 pr-2" style={{ padding: `${fs * 0.15}px 0` }}>
              <FitText lines={lines.length > 1 ? lines : undefined} text={lines.length === 1 ? lines[0] : undefined} maxPx={fs * 0.8} minPx={10} maxLines={lines.length > 1 ? 2 : 2} className={leader ? 'font-bold' : 'font-semibold'} style={{ color: leader ? GREEN : '#1f2a24', lineHeight: 1.15 }} testid={`pv-standings-row-${row.entry_id}-team`} />
            </div>
            <div className="text-center tabular-nums font-bold" style={{ fontSize: fs * 0.9, color: GREEN }}>{row.wins}</div>
            <div className="text-center tabular-nums font-semibold text-[#6a766e]" style={{ fontSize: fs * 0.9 }}>{row.losses}</div>
            <div className="text-center tabular-nums font-semibold text-[#2a332c]" style={{ fontSize: fs * 0.8 }} data-testid={`pv-standings-row-${row.entry_id}-gw`}>{row.games_won ?? 0}</div>
            <div className="text-center tabular-nums font-semibold text-[#6a766e]" style={{ fontSize: fs * 0.8 }} data-testid={`pv-standings-row-${row.entry_id}-gl`}>{row.games_lost ?? 0}</div>
            <div className="text-center tabular-nums font-bold" style={{ fontSize: fs * 0.8, color: row.game_win_pct == null ? '#9aa39d' : GREEN }} data-testid={`pv-standings-row-${row.entry_id}-pct`}>{fmtPct(row.game_win_pct)}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function PresentationStandings({ rr }) {
  const boxRef = useRef(null);
  const { w, h } = useSize(boxRef);
  const standings = rr?.standings || {};
  const labels = rr?.pool_labels || {};
  const entriesById = Object.fromEntries((rr?.entries || []).map((e) => [e.id, e]));
  const pools = Object.keys(standings).sort((a, b) => Number(a) - Number(b));

  if (pools.length === 0) {
    return (
      <div data-testid="pv-standings-empty" className="h-full flex items-center justify-center text-[#6a766e]" style={{ fontSize: 'clamp(14px, 1.2vw, 18px)' }}>
        No standings available yet.
      </div>
    );
  }

  const { cols, rows } = gridShape(pools.length);
  const maxRows = Math.max(...pools.map((p) => (standings[p] || []).length), 1);
  const gap = 18;
  const cellH = h ? (h - gap * (rows - 1)) / rows : 0;
  const cellW = w ? (w - gap * (cols - 1)) / cols : 0;
  // Font size so the tallest pool fits its cell height; also bounded by width.
  const byHeight = cellH / (POOL_HEADER_H + TABLE_HEADER_H + ROW_H * maxRows + 0.2);
  const byWidth = cellW / 21;
  const fs = Math.max(11, Math.min(38, byHeight, byWidth));

  return (
    <div ref={boxRef} className="h-full w-full min-h-0 flex items-center" data-testid="pv-standings">
      <div
        className="w-full grid"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap, alignItems: 'start' }}
      >
        {h > 0 && pools.map((p) => (
          <PoolCard key={p} label={labels[String(p)] || `Pool ${p}`} rows={standings[p] || []} entriesById={entriesById} fs={fs} />
        ))}
      </div>
    </div>
  );
}
